import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRole } from '@/hooks/useRole';
import { fetchDevices, fetchDeviceById, Device, DeviceListData } from '@/services/api/deviceService';
import { 
  Button, 
  Card, 
  Alert, 
  Table, 
  THead, 
  TBody, 
  Tr, 
  Th, 
  Td,
  Input,
  Spinner,
  Tabs,
  TabList,
  TabNav,
  TabContent,
  Pagination
} from '@/components/ui';
import { 
  HiSearch, 
  HiRefresh, 
  HiClock, 
  HiInformationCircle 
} from 'react-icons/hi';

// Tab panel interface
interface TabPanelProps {
  children?: React.ReactNode;
  value: number | string;
}

// Tab panel component
function TabPanel(props: TabPanelProps) {
  const { children, value } = props;

  return (
    <TabContent value={value}>
      <div className="p-4">
        {children}
      </div>
    </TabContent>
  );
}

const DevicePage = () => {
  const { isAdmin, isLoading: isRoleLoading } = useRole();
  const navigate = useNavigate();
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Tab state
  const [tabValue, setTabValue] = useState('all-devices');
  
  // Single device search state
  const [deviceIdToSearch, setDeviceIdToSearch] = useState('');
  const [singleDevice, setSingleDevice] = useState<Device | null>(null);
  const [singleDeviceLoading, setSingleDeviceLoading] = useState(false);
  const [singleDeviceError, setSingleDeviceError] = useState<string | null>(null);

  // Handle tab change
  const handleTabChange = (val: string | number) => {
    setTabValue(val as string);
  };

  // Load devices from API
  const loadDevices = async () => {
    if (!isAdmin()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response: DeviceListData = await fetchDevices({
        page: page + 1, // API uses 1-based indexing
        pageSize: rowsPerPage,
        search: undefined, // No search parameter for all devices
      });
      
      setDevices(response.devices);
      setTotalDevices(response.pagination.total);
      setTotalPages(response.pagination.totalPage);
    } catch (err) {
      setError('Failed to load devices. Please try again later.');
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  };

  // Search for a single device by ID
  const searchDeviceById = async () => {
    if (!deviceIdToSearch.trim()) {
      setSingleDeviceError('Please enter a device ID');
      return;
    }
    
    setSingleDeviceLoading(true);
    setSingleDeviceError(null);
    setSingleDevice(null);
    
    try {
      const device = await fetchDeviceById(deviceIdToSearch.trim());
      setSingleDevice(device);
    } catch (err) {
      setSingleDeviceError('Failed to find device with the specified ID');
      console.error('Error searching for device:', err);
    } finally {
      setSingleDeviceLoading(false);
    }
  };

  // Load devices on initial render and when pagination changes
  useEffect(() => {
    if (isAdmin() && tabValue === 'all-devices') {
      loadDevices();
    }
  }, [page, rowsPerPage, isAdmin, tabValue]);

  // 处理单个设备搜索的回车键
  const handleSingleDeviceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchDeviceById();
    }
  };

  // 重置单个设备搜索
  const handleSingleDeviceReset = () => {
    setDeviceIdToSearch('');
    setSingleDevice(null);
    setSingleDeviceError(null);
  };

  // Handle page change
  const handleChangePage = (newPage: number) => {
    setPage(newPage - 1); // Convert to 0-based for internal state
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 处理查看操作数据
  const handleViewOperations = (deviceId: string) => {
    navigate(`/operation/${deviceId}/operations`);
  };

  // 显示加载状态
  if (isRoleLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner size={40} />
      </div>
    );
  }

  // 如果不是管理员，显示访问拒绝信息
  if (!isAdmin()) {
    return (
      <div className="p-4">
        <h4 className="text-2xl text-red-600 mb-2">Access Denied</h4>
        <p className="mt-2">
          You do not have permission to view this page. This page is only accessible to administrators.
        </p>
      </div>
    );
  }

  // Render device table
  const renderDeviceTable = (devices: Device[]) => (
    <div className="overflow-x-auto">
      <Table hoverable>
        <THead>
          <Tr>
            <Th><strong>ID</strong></Th>
            <Th><strong>Device ID</strong></Th>
            <Th><strong>Account</strong></Th>
            <Th><strong>Last Message Time</strong></Th>
            <Th><strong>Created At</strong></Th>
            <Th><strong>Updated At</strong></Th>
            <Th><strong>Actions</strong></Th>
          </Tr>
        </THead>
        <TBody>
          {devices.length > 0 ? (
            devices.map((device) => (
              <Tr key={device.id}>
                <Td>{device.id}</Td>
                <Td>{device.device_id}</Td>
                <Td>{device.account}</Td>
                <Td>{new Date(device.last_message_time).toLocaleString()}</Td>
                <Td>{new Date(device.created_at).toLocaleString()}</Td>
                <Td>{new Date(device.updated_at).toLocaleString()}</Td>
                <Td>
                  <Button 
                    size="sm" 
                    variant="twoTone" 
                    icon={<HiClock />}
                    onClick={() => handleViewOperations(device.device_id)}
                    title="View Operation History"
                  >
                    History
                  </Button>
                </Td>
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={7} className="text-center">
                No devices found
              </Td>
            </Tr>
          )}
        </TBody>
      </Table>
    </div>
  );

  return (
    <div className="w-full p-4">
      <h4 className="text-2xl mb-4">Devices</h4>
      
      <Tabs value={tabValue} onChange={handleTabChange}>
        <TabList>
          <TabNav value="all-devices">All Devices</TabNav>
          <TabNav value="search-device">Search Device by ID</TabNav>
        </TabList>
      </Tabs>
      
      {tabValue === 'all-devices' && (
        <TabPanel value="all-devices">
          {/* Error message */}
          {error && (
            <Alert className="mb-4" type="danger" showIcon>
              {error}
            </Alert>
          )}
          
          {/* 设备表格 */}
          <Card>
            {loading ? (
              <div className="flex justify-center items-center p-4 h-52">
                <Spinner size={40} />
              </div>
            ) : (
              <>
                {renderDeviceTable(devices)}
                
                {/* Pagination */}
                <div className="flex justify-end p-4">
                  <Pagination
                    currentPage={page + 1}
                    total={totalDevices}
                    pageSize={rowsPerPage}
                    onChange={handleChangePage}
                  />
                </div>
              </>
            )}
          </Card>
        </TabPanel>
      )}
      
      {tabValue === 'search-device' && (
        <TabPanel value="search-device">
          {/* 单个设备搜索区域 */}
          <Card className="mb-4">
            <div className="flex items-center p-4">
              <div className="relative flex-1">
                <Input
                  placeholder="Enter Device ID"
                  value={deviceIdToSearch}
                  onChange={(e) => setDeviceIdToSearch(e.target.value)}
                  onKeyDown={handleSingleDeviceKeyPress}
                  size="md"
                  style={{ paddingRight: '2.5rem' }}
                />
                <div 
                  style={{ 
                    position: 'absolute', 
                    right: '10px', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    cursor: 'pointer'
                  }}
                  onClick={searchDeviceById}
                >
                  <HiSearch className="text-lg text-gray-500 hover:text-primary-500" />
                </div>
              </div>
              <Button 
                variant="solid" 
                onClick={searchDeviceById}
                disabled={singleDeviceLoading}
                className="mx-2"
              >
                {singleDeviceLoading ? <Spinner size={20} className="mr-1" /> : 'Search'}
              </Button>
              <Button 
                variant="plain" 
                icon={<HiRefresh />}
                onClick={handleSingleDeviceReset} 
                disabled={singleDeviceLoading}
              />
            </div>
          </Card>
          
          {/* Error message */}
          {singleDeviceError && (
            <Alert className="mb-4" type="danger" showIcon>
              {singleDeviceError}
            </Alert>
          )}
          
          {/* 单个设备结果 */}
          {singleDeviceLoading ? (
            <div className="flex justify-center p-4">
              <Spinner size={40} />
            </div>
          ) : singleDevice ? (
            <Card>
              {renderDeviceTable([singleDevice])}
            </Card>
          ) : null}
        </TabPanel>
      )}
    </div>
  );
};

export default DevicePage;